/**
 * TailwaterLiveExtras: extra live-river details for the ATF map popup.
 *
 * Render it inside your existing Tailwater live component, after the
 * flow/level pills, passing the same `tailwater_live` object:
 *
 *   <TailwaterLiveExtras live={poi.tailwater_live} />
 *
 * Uses fields added in Tailwater ATF edition 1.9.4 (all optional; anything
 * missing is simply not shown):
 *   dissolved_oxygen: { value: 11.8, unit: "mg/L", note: "well oxygenated" }
 *   clarity:          { ntu: 41.5, label: "Coloured" }
 *   water_temp_change_48h: -1.0
 *   trout_conditions: { rating: "Prime", level: 3, label: "River conditions for trout: Prime",
 *                       reasons: ["water 12.3°C - in trout's prime feeding range", "well oxygenated"],
 *                       note: "Based on the river readings. The full feeding rating in Tailwater also uses weather, pressure and moon." }
 *   water_url: link that opens this water in Tailwater
 *   cta: "Full conditions, hatches and feeding rating in Tailwater"
 *   fetched_at: "2026-10-15T06:10:00+11:00"
 */
import React from 'react';
import '../tailwaterLiveExtras.css';

const LEVEL_CLASS = ['poor', 'fair', 'good', 'prime'];

function fetchedLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-AU', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

export default function TailwaterLiveExtras({ live }) {
  console.log('extras', live); 
  if (!live) return null;
  const doo = live.dissolved_oxygen;
  const clarity = live.clarity;
  const trout = live.trout_conditions;
  const change = live.water_temp_change_48h;
  const fetched = fetchedLabel(live.fetched_at);

  return (
    <div className="atf-tailwater-extras">
      {(doo || clarity || change != null) && (
        <div className="atf-tailwater-live-pills">
          {doo && (
            <div className="atf-tailwater-live-pill">
              <span className="label">Dissolved oxygen</span>
              <span className="value">{doo.value} {doo.unit}</span>
              {doo.note && <span className="sub">{doo.note}</span>}
            </div>
          )}
          {clarity && (
            <div className="atf-tailwater-live-pill">
              <span className="label">Clarity</span>
              <span className="value">{clarity.label}</span>
              <span className="sub">{clarity.ntu} NTU</span>
            </div>
          )}
          {change != null && (
            <div className="atf-tailwater-live-pill">
              <span className="label">Water temp change, 48h</span>
              <span className="value">{change > 0 ? '+' : ''}{change}°C</span>
            </div>
          )}
        </div>
      )}

      {trout && (
        <div className={`atf-tailwater-trout atf-tailwater-trout--${LEVEL_CLASS[trout.level] || 'fair'}`}>
          <div className="atf-tailwater-trout-label">{trout.label}</div>
          {trout.reasons && trout.reasons.length > 0 && (
            <ul>{trout.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
          )}
          {trout.note && <div className="atf-tailwater-trout-note">{trout.note}</div>}
        </div>
      )}

      {(live.water_url || live.page) && (
        <a className="atf-tailwater-cta" href={live.water_url || live.page} target="_blank" rel="noreferrer">
          {live.cta || 'Full conditions in Tailwater'} &rarr;
        </a>
      )}
      {fetched && <div className="atf-tailwater-fetched">Reading fetched {fetched}</div>}
    </div>
  );
}
